import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRight, CheckCircle, BookOpen, ChevronDown,
  Bell,
  RotateCcw, Menu, X,
  Settings, LogOut, LayoutDashboard,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';
import { useSkills } from '@hooks/useSkills';
import { useDarkMode } from '@hooks/useDarkMode';   // the shared hook; a local copy used to live here with the same useState(false) flaw
import { getSkillCard } from '@data/configs/index.js';
import { FAQ_DATA, LANDING_INLINE_FAQ_IDS } from '@data/faqData';
import {
  ALL_CHARACTERS, CHARACTER_COUNTS, isScenarioLive,
} from '@features/sales-negotiation/data';

/* ─── PostHog ─── */
const track = (e, p = {}) => {
  if (typeof window !== 'undefined' && window.posthog) window.posthog.capture(e, p);
};


/* ─── Reduced motion ───
   Read as state, not as a one-off boolean, so a user toggling the
   OS setting mid-session gets the static page without a reload.
   All three pinned stages and the score ring key off this. */
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

/* ─── Viewport predicate ───
   Used by the territory stage: the real skill cards have intrinsic
   heights of 340-500px, so four of them only fit inside one pinned
   viewport when they sit on a single row. Below that width they wrap,
   two rows overflow the pin, and the tallest card ends up half
   outside it. So the pin engages by media query rather than always. */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

/* ─── Scroll reveal ─── */
const useScrollReveal = () => {
  const seen = useRef(new Set());
  useEffect(() => {
    const io = new IntersectionObserver(entries =>
      entries.forEach(e => {
        if (e.isIntersecting && !seen.current.has(e.target)) {
          seen.current.add(e.target);
          e.target.classList.add('revealed');
          io.unobserve(e.target);
        }
      }), { threshold: 0.01 }
    );
    document.querySelectorAll('.reveal').forEach(el => {
      if (!seen.current.has(el)) io.observe(el);
    });
    return () => io.disconnect();
  }, []);
};

/* ─── JSON-LD ─── */
const useSchema = () => {
  useEffect(() => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Castle Atlas',
      applicationCategory: 'EducationalApplication',
      url: 'https://castleatlas.co',
      description: 'AI-powered skill learning platform with real-time scoring and voice roleplay.',
      offers: [{ '@type': 'Offer', price: '0', priceCurrency: 'USD' }],
    });
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);
};

/* ─── Tokens ────────────────────────────────────────────────
   This page is ONE continuous terminal session, dark from the
   header to the footer, in light mode and dark mode alike. There
   is deliberately no light variant: the previous two-palette
   setup is what produced a white nav slamming into a black hero,
   and lightening the hero or darkening only the header would each
   have been a negotiation with that problem rather than a fix.

   Every value is sourced from the real TT object in the AI
   Prompting skill rather than re-typed, so the landing page's
   chrome cannot drift from the actual product surface it is
   claiming to show. See terminalTokens.js.

   `useDarkMode` stays wired because the shared hook is used by
   other pages, but this page's chrome ignores it. Sections that
   still take an `isDark` prop are ones not yet migrated; they
   resolve to the same dark values either way. */
const C = {
  bg: TT.bg,               // #0d0d0d  the page itself
  surface: TT.bgSurface,   // #1a1a1a  panels, cards, mounted plates
  raised: TT.bgElevated, /* #111111 subtle raise off the page */
  rule: TT.border,         // #2a2a2a  every hairline
  ruleHover: TT.borderHover,
  ink: TT.text,            // #e2e8f0  primary copy
  graphite: TT.textMuted,  // #64748b  secondary copy
  dim: TT.textDim,         // #475569  mono labels, tertiary
  orange: TT.orange,       // #ea580c  the only warm thing on the page
  orangeInk: '#fb923c',    // orange that stays legible as text on #0d0d0d
  orangeWash: 'rgba(234,88,12,0.10)',
  green: TT.green,
  amber: TT.amber,
  red: '#ef4444',
};
/* Rounding. An earlier pass used 4px panels / 3px buttons, which on a
   black page made every card read as a flat rectangle rather than an
   object sitting on the page. The reference is the final CTA panel.
   `term` and `inner` come from TT so the terminal cannot drift from
   the sandbox it is imitating. */
const R = {
  hero: 32,               // full-width dark panels: final CTA, founder, notify
  panel: 24,              // cards: pricing, comparison, plate frame
  term: TT.radius.xl,     // 18, the terminal window, matching the sandbox
  inner: TT.radius.md,    // 10, nested elements inside a card
  button: 12,
  pill: 99,
};

/* ─── One type scale ─────────────────────────────────────────
   Every text size on the page is one of these eight rungs. Each
   carries size (clamp()'d so it never needs a manual breakpoint
   override), line-height, weight and letter-spacing together, so
   using a rung can't accidentally drop one of the four. Spread into
   a style object and override individual fields per-instance only
   for things the scale doesn't own (color, margin, textTransform). */
const TYPE = {
  displayXL: { fontFamily: 'var(--font-display)', fontSize: 'clamp(2.6rem,6vw,4.75rem)', lineHeight: 1.02, fontWeight: 900, letterSpacing: '-0.03em' },
  displayL:  { fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem,4.5vw,3rem)',     lineHeight: 1.08, fontWeight: 900, letterSpacing: '-0.03em' },
  displayM:  { fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem,3.8vw,2.4rem)', lineHeight: 1.15, fontWeight: 900, letterSpacing: '-0.02em' },
  heading:   { fontFamily: 'var(--font-display)', fontSize: 'clamp(1rem,1.6vw,1.2rem)',   lineHeight: 1.3,  fontWeight: 800, letterSpacing: '-0.01em' },
  bodyL:     { fontFamily: 'var(--font-body)',    fontSize: 'clamp(0.95rem,1.1vw,1.05rem)', lineHeight: 1.75, fontWeight: 400, letterSpacing: 'normal' },
  body:      { fontFamily: 'var(--font-body)',    fontSize: '0.875rem',  lineHeight: 1.6,  fontWeight: 400, letterSpacing: 'normal' },
  bodyS:     { fontFamily: 'var(--font-body)',    fontSize: '0.8125rem', lineHeight: 1.5,  fontWeight: 500, letterSpacing: 'normal' },
  label:     { fontFamily: 'var(--font-mono)',    fontSize: '0.6875rem', lineHeight: 1.3,  fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' },
  monoS:     { fontFamily: 'var(--font-mono)',    fontSize: '0.6875rem', lineHeight: 1.4,  fontWeight: 600, letterSpacing: '0.08em' },
};

/* ─── Ambient grid, replacing ContourBG on THIS page only.
   ContourBG stays in the repo and is still used by 13 other
   pages (Dashboard, Skills, Billing, Contact, Feedback, Login,
   Signup, Settings, NotFound, Privacy, Terms), so it must not
   be deleted. Radial mask keeps it from tiling to the edges. ─── */
const GridBG = ({ opacity = 1, size = 32 }) => {
  const line = 'rgba(226,232,240,0.05)';
  const mask = 'radial-gradient(ellipse 70% 70% at 50% 42%, #000 0%, transparent 78%)';
  return (
    <div aria-hidden="true" style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity,
      backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      WebkitMaskImage: mask, maskImage: mask,
    }}/>
  );
};

/* ─── Real Castle Logo (from /public/logo.svg) ─── */
const CastleLogoMark = ({ size = 26 }) => {
  const h = Math.round(size * (68 / 60));
  return (
    <svg width={size} height={h} viewBox="0 0 60 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill="#ea580c" d="m0 0h10v8h6v-8h10v8h8v-8h10v8h6v-8h10v42q0 16-14 22l-13.17 3.29c1.89-1.01 3.17-3 3.17-5.29v-6c0-3.31-2.69-6-6-6-3.31 0-6 2.69-6 6v6c0 2.29 1.28 4.28 3.17 5.29l-13.17-3.29q-14-6-14-22z"/>
    </svg>
  );
};

/* ─── Skills ─── */
/* Source: @features/sales-negotiation/data CHARACTER_COUNTS, summed live off the four
   character-roster files — never hardcoded, so this can't go stale like the Supabase
   `skills.character_count` column has (DB says 2, the real rosters hold 60+). Used by
   the Opposition section and the Straight Answers comparison row below. */
// the characters people can play today (a coming-soon scenario is not counted): 85 while two scenarios are not open
const CHARACTERS_TOTAL = CHARACTER_COUNTS.live;

/* The 85% plate. Filip is supplying the illustration; until it lands
   the section renders single-column rather than shipping a broken
   image. Flip HAS_UNFINISHED_PLATE by dropping the file in. */
const UNFINISHED_PLATE = '/plates/plate-01-unfinished.webp';
const HAS_UNFINISHED_PLATE = false;

/* Two figures, both about the platform rather than one skill — the
   character count and the live-reps total used to sit here too, but a
   Voice Room number and an AI-Prompting-heavy sum both read as pitching
   one territory rather than the hub. The character count already has a
   home in the Opposition section below; the reps total didn't have one
   worth keeping once its two components were cut down to this. */
const FIGURES = [
  {
    value: '0-100', label: 'on every rep',
    sub: 'one scoring engine, the same one, behind every skill on the platform',
  },
  {
    value: '$0', label: 'to start',
    sub: 'no card, nothing to install',
  },
];

/* clamp+normalise v into 0–1 across [a,b] */
const seg = (v, a, b) => Math.max(0, Math.min(1, (v - a) / (b - a)));
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/* ═══════════════════════════════════════════════════════════
   HERO: scroll-driven rep  [PIN 1 of 3]
   ═══════════════════════════════════════════════════════════
   Restored verbatim from commit 0bd9a8e (pre-archipelago). The whole
   hero is a pure function of one number: `p`, the 0–1 scroll progress
   through the tall outer stage. Because every phase derives from `p`
   rather than from timers or a state machine, the entire sequence
   plays backwards on scroll-up for free, and there is no state to
   desync.

   Phase map (all in units of p):
     headline out     0.00 -> 0.14
     terminal in      0.10 -> 0.22
     $ command types  0.22 -> 0.30
     prompt types     0.32 -> 0.56
     response streams 0.58 -> 0.76
     score climbs     0.78 -> 0.92   (0 -> 89)
     verdict          0.92 -> 0.95
     eval --criteria  0.95 -> 1.00   (3 lines, staggered)

   Perf: ONE passive scroll listener, rAF-throttled, and the handler
   performs a single getBoundingClientRect read before any writes, so
   it never interleaves reads and writes and never forces a second
   layout per frame.

   Terminal chrome uses the imported TT tokens (the real PromptSandbox
   palette), NOT a local copy. The `$` command line, the `// comment`
   styling and the ✓/▢ criteria rows are reproduced from Term.jsx and
   PromptSandbox.jsx so this reads as Castle Atlas's terminal rather
   than a generic one. */
const HERO_COMMAND = 'rep start --lesson 01';
/* Real scoring criteria shape, matching the sandbox's `spec` block:
   a ▢ before it is evaluated, a ✓ once it passes. */
const HERO_CRITERIA = [
  'Names the recipient and the exact day',
  'States what happens to the open work',
  'Constrains tone and length',
];
const HERO_PROMPT =
  'Write a short, polite email to my manager Sarah asking for this Friday off. ' +
  'Confirm every task on my plate will be done by Thursday EOD.';
const HERO_RESPONSE =
  "Subject: Friday off\n\nHi Sarah,\n\nI'd like to take this Friday off. Everything on my " +
  "plate will be finished by Thursday EOD, so nothing should slip.\n\nThanks,\nAlex";
const HERO_SCORE = 89;

/* Score threshold colours, matching PromptSandbox exactly:
   >=80 accent, >=60 amber, else red. */
const scoreColor = (n, accent = C.orange) =>
  n >= 80 ? accent : n >= 60 ? C.amber : C.red;

const ScrollRepHero = ({ reduced }) => {
  const [chromeHover, setChromeHover] = useState(false);
  const stageRef = useRef(null);
  const headRef = useRef(null);
  const termRef = useRef(null);
  const bloomRef = useRef(null);
  const cueRef = useRef(null);
  const cmdRef = useRef(null);
  const cmdCaretRef = useRef(null);
  const promptRef = useRef(null);
  const caretRef = useRef(null);
  const respRef = useRef(null);
  const barRef = useRef(null);
  const numRef = useRef(null);
  const verdictRef = useRef(null);
  const evalRef = useRef(null);
  const critRefs = useRef([]);

  useEffect(() => {
    if (reduced) return undefined;
    const stage = stageRef.current;
    if (!stage) return undefined;
    let queued = false;

    const paint = () => {
      queued = false;
      /* single layout read, then writes only */
      const rect = stage.getBoundingClientRect();
      const travel = stage.offsetHeight - window.innerHeight;
      const p = travel > 0 ? Math.max(0, Math.min(1, -rect.top / travel)) : 0;

      const headOut = seg(p, 0, 0.14);
      if (headRef.current) {
        headRef.current.style.opacity = String(1 - headOut);
        headRef.current.style.transform = `translateY(${-headOut * 70}px)`;
        headRef.current.style.pointerEvents = headOut > 0.95 ? 'none' : 'auto';
      }
      if (cueRef.current) cueRef.current.style.opacity = String(1 - seg(p, 0, 0.1));

      const termIn = easeInOut(seg(p, 0.1, 0.22));
      if (termRef.current) {
        termRef.current.style.opacity = String(termIn);
        termRef.current.style.transform =
          `translateY(${(1 - termIn) * 40}px) scale(${0.97 + termIn * 0.03})`;
      }

      /* the session opens with a real command, the way the sandbox does */
      const cmdTyped = seg(p, 0.22, 0.3);
      if (cmdRef.current) {
        cmdRef.current.textContent = HERO_COMMAND.slice(0, Math.round(HERO_COMMAND.length * cmdTyped));
      }
      if (cmdCaretRef.current) {
        cmdCaretRef.current.style.opacity = cmdTyped > 0 && cmdTyped < 1 ? '1' : '0';
      }

      const typed = seg(p, 0.32, 0.56);
      if (promptRef.current) {
        promptRef.current.textContent = HERO_PROMPT.slice(0, Math.round(HERO_PROMPT.length * typed));
      }
      if (caretRef.current) {
        caretRef.current.style.opacity = typed > 0 && typed < 1 ? '1' : typed >= 1 ? '0' : '0.35';
      }

      const streamed = seg(p, 0.58, 0.76);
      if (respRef.current) {
        respRef.current.textContent = HERO_RESPONSE.slice(0, Math.round(HERO_RESPONSE.length * streamed));
      }

      const climb = easeInOut(seg(p, 0.78, 0.92));
      const shown = Math.round(climb * HERO_SCORE);
      /* Threshold colour, same rule as the sandbox: the number is red
         while it is failing, amber as it crosses 60, and lands on the
         accent at 80+. The climb therefore reads as a state change,
         not just a counter. */
      const sc = scoreColor(shown, TT.orange);
      if (numRef.current) {
        numRef.current.textContent = String(shown);
        numRef.current.parentElement.style.color = sc;
      }
      if (barRef.current) {
        barRef.current.style.width = `${climb * HERO_SCORE}%`;
        barRef.current.style.background = sc;
      }
      if (bloomRef.current) bloomRef.current.style.opacity = String(climb * 0.9);
      if (verdictRef.current) verdictRef.current.style.opacity = String(seg(p, 0.92, 0.95));

      /* eval --criteria: the block fades in, then each criterion flips
         from ▢ to ✓ in turn. This is the claim the whole page makes,
         that you are told WHY, so it is the last thing the reader sees. */
      if (evalRef.current) evalRef.current.style.opacity = String(seg(p, 0.95, 0.965));
      const evalP = seg(p, 0.955, 1);
      critRefs.current.forEach((el, i) => {
        if (!el) return;
        const done = evalP >= (i + 1) / (HERO_CRITERIA.length + 0.35);
        el.textContent = done ? '✓' : '▢';
        el.style.color = done ? TT.green : TT.textDim;
      });
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    paint();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduced]);

  /* Reduced motion: the stage collapses to auto height, the pin
     becomes static, and everything renders at its END state so
     the reader still sees a finished, scored rep. */
  const finalP = reduced ? HERO_PROMPT : '';
  const finalR = reduced ? HERO_RESPONSE : '';

  return (
    <section
      ref={stageRef}
      aria-label="A worked example: one scored rep"
      className="story-stage"
      style={{
        position: 'relative',
        height: reduced ? 'auto' : 'var(--stage-h)',
        background: TT.bg,
      }}
    >
      <div style={{
        position: reduced ? 'static' : 'sticky', top: 0,
        height: reduced ? 'auto' : '100vh',
        overflow: 'hidden', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        /* The header is sticky at the same top:0, so without reserving
           its height here the terminal centres against the full viewport
           and its chrome bar gets clipped underneath the header. Padding
           (with border-box) centres the content in the space that is
           actually visible instead. */
        boxSizing: 'border-box',
        padding: reduced ? '80px 0' : 'var(--header-h) 0 0',
        /* In reduced mode the headline stops being absolutely
           positioned and becomes a real flex item, so without this the
           two children lay out side by side and the terminal gets
           clipped at the right edge. Stack them instead. */
        flexDirection: reduced ? 'column' : 'row',
      }}>
        {/* The sandbox's page texture: a 22px dot grid on the PAGE,
            behind the window. It used to be inside the terminal body,
            which is the inverse of how the real product looks. */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}/>
        {/* orange bloom, grows with the score */}
        <div ref={bloomRef} aria-hidden="true" style={{
          position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
          width: 1100, height: 1100, borderRadius: '50%', pointerEvents: 'none',
          background: `radial-gradient(circle, ${TT.orangeGlow.replace('0.35', '0.16')}, transparent 62%)`,
          opacity: reduced ? 0.9 : 0,
        }}/>

        {/* ── headline layer ── */}
        <div
          ref={headRef}
          className={reduced ? '' : 'hero-head'}
          style={{
            position: reduced ? 'static' : 'absolute', inset: reduced ? 'auto' : 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', textAlign: 'center', padding: '0 28px', zIndex: 5,
            marginBottom: reduced ? 56 : 0,
          }}
        >
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: TT.orange, marginBottom: 26,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span aria-hidden="true" style={{
              width: 6, height: 6, borderRadius: '50%', background: TT.green,
              boxShadow: `0 0 8px ${TT.green}`,
            }}/>
            Live now · Free · No card
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(2.6rem,7.5vw,5.6rem)',
            fontWeight: 900, lineHeight: 0.98, letterSpacing: '-0.04em',
            color: '#fff', maxWidth: '15ch', textWrap: 'balance',
          }}>
            Nobody finishes the course.<br/>
            <span style={{ color: TT.orange }}>Finish one rep.</span>
          </h1>
          <p style={{
            marginTop: 26, fontSize: 'clamp(1rem,1.7vw,1.2rem)', color: TT.textMuted,
            maxWidth: '52ch', lineHeight: 1.65,
          }}>
            Up to 85% of people never reach the end of what they bought, then quietly decide
            it says something about them. It doesn&rsquo;t. Keep scrolling and do one.
          </p>
        </div>

        {/* ── terminal layer ── */}
        <div
          ref={termRef}
          onMouseEnter={() => setChromeHover(true)}
          onMouseLeave={() => setChromeHover(false)}
          style={{
            position: 'relative', zIndex: 4, width: 'min(920px, 92vw)',
            background: TT.bg, border: `1px solid ${TT.border}`,
            borderRadius: R.term, overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            opacity: reduced ? 1 : 0,
            transform: reduced ? 'none' : 'translateY(40px) scale(0.97)',
          }}
        >
          {/* Chrome bar, matching TermWindow in Term.jsx and the card in
              config.ai-prompting.jsx exactly: 12px traffic lights at gap 7
              in the real macOS colours, a CENTRED title, and the same
              hover glow. The previous grey 9px dots were the single
              biggest reason this did not read as our terminal. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, background: TT.bgSurface,
            borderBottom: `1px solid ${TT.border}`, padding: '10px 16px',
          }}>
            <div style={{ display: 'flex', gap: 7 }}>
              {['#ff5f56', '#ffbd2e', '#27c93f'].map((c) => (
                <div key={c} aria-hidden="true" style={{
                  width: 12, height: 12, borderRadius: '50%', background: c,
                  boxShadow: chromeHover ? `0 0 8px ${c}80` : 'none',
                  transition: 'all 0.18s ease',
                }}/>
              ))}
            </div>
            <span className="hero-chrome-path" style={{
              flex: 1, textAlign: 'center', fontSize: 11, color: '#555',
              letterSpacing: '0.05em', fontFamily: TT.fontMono,
            }}>castle-atlas / prompt-engineering</span>
          </div>

          {/* Body sits on TT.bg, not bgSurface: in the sandbox the window
              is the dark plane and the TITLE BAR is the lighter one. The
              22px dot grid belongs to the page behind the window, not
              inside it, so it now lives on the section. */}
          <div className="hero-term-body" style={{
            background: TT.bg, padding: 'clamp(18px,2.6vw,30px)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{
                fontFamily: TT.fontMono, fontSize: 10, letterSpacing: '0.16em',
                color: TT.textDim, textTransform: 'uppercase',
              }}>Rep 01 · Vague vs specific</span>
              <span aria-hidden="true" style={{ display: 'flex', gap: 5 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: i === 0 ? TT.orange : TT.border,
                  }}/>
                ))}
              </span>
            </div>

            {/* the command that starts the rep, typed like the sandbox's own */}
            <div className="hero-cmd" style={{
              display: 'flex', gap: 8, alignItems: 'baseline', fontFamily: TT.fontMono,
              fontSize: 13, lineHeight: 1.7, marginBottom: 14, minHeight: '1.7em',
            }}>
              <span style={{ color: TT.textDim, fontWeight: 700, userSelect: 'none' }}>$</span>
              <span style={{ color: TT.text }}>
                <span ref={cmdRef}>{reduced ? HERO_COMMAND : ''}</span>
                <span ref={cmdCaretRef} aria-hidden="true" style={{
                  display: 'inline-block', width: 7, height: '1em', background: TT.orange,
                  verticalAlign: '-0.1em', marginLeft: 2, opacity: 0,
                }}/>
              </span>
            </div>

            <p className="hero-brief" style={{
              fontSize: 14, color: TT.textMuted, lineHeight: 1.65,
              borderLeft: `2px solid ${TT.orange}`, paddingLeft: 14, marginBottom: 24,
            }}>
              Ask an AI to write an email requesting a day off. Be specific enough that it
              needs no follow-up question.
            </p>

            <div className="hero-slot" style={{ marginBottom: 20 }}>
              <div className="hero-slot-label" style={{
                fontFamily: TT.fontMono, fontSize: 9.5, letterSpacing: '0.18em',
                color: TT.textDim, textTransform: 'uppercase', marginBottom: 9,
              }}>Your prompt</div>
              <div className="hero-pane hero-pane-prompt" style={{
                fontFamily: TT.fontMono, fontSize: 'clamp(12px,1.35vw,14px)', lineHeight: 1.7,
                color: TT.text, minHeight: '4.8em', whiteSpace: 'pre-wrap',
              }}>
                <span ref={promptRef}>{finalP}</span>
                <span ref={caretRef} aria-hidden="true" style={{
                  display: 'inline-block', width: 8, height: '1.05em', background: TT.orange,
                  verticalAlign: '-2px', marginLeft: 2, opacity: reduced ? 0 : 0.35,
                }}/>
              </div>
            </div>

            <div className="hero-slot" style={{ marginBottom: 20 }}>
              <div className="hero-slot-label" style={{
                fontFamily: TT.fontMono, fontSize: 9.5, letterSpacing: '0.18em',
                color: TT.textDim, textTransform: 'uppercase', marginBottom: 9,
              }}>Response</div>
              <div ref={respRef} className="hero-pane hero-pane-resp" style={{
                fontSize: 'clamp(12.5px,1.35vw,14px)', lineHeight: 1.7, color: TT.textMuted,
                minHeight: '6.5em', whiteSpace: 'pre-wrap',
              }}>{finalR}</div>
            </div>

            <div className="hero-score-row" style={{
              display: 'flex', alignItems: 'center', gap: 'clamp(14px,2.5vw,28px)',
              borderTop: `1px solid ${TT.border}`, paddingTop: 22,
            }}>
              <span style={{
                fontFamily: TT.fontMono, fontSize: 9.5, letterSpacing: '0.18em',
                color: TT.textDim, textTransform: 'uppercase',
              }}>Score</span>
              <span style={{ flex: 1, height: 6, background: TT.bgSurface, borderRadius: 2, overflow: 'hidden' }}>
                <span ref={barRef} style={{
                  display: 'block', height: '100%', borderRadius: 2,
                  background: reduced ? scoreColor(HERO_SCORE, TT.orange) : C.red,
                  width: reduced ? `${HERO_SCORE}%` : 0,
                }}/>
              </span>
              {/* Mono, not the display face. The sandbox renders its score
                  in TT.fontMono at weight 900, and the display face is what
                  made this read as a marketing page rather than a terminal.
                  The SIZE stays large on purpose: the climb is the hero's
                  payoff, and the sandbox's 22px would throw that away. */}
              <span className="hero-score-num" style={{
                fontFamily: TT.fontMono, fontWeight: 900,
                fontSize: 'clamp(2.8rem,7.5vw,5.4rem)', lineHeight: 0.85,
                letterSpacing: '-0.05em',
                color: reduced ? scoreColor(HERO_SCORE, TT.orange) : C.red,
                fontVariantNumeric: 'tabular-nums', minWidth: '3.1ch', textAlign: 'right',
              }}>
                <span ref={numRef}>{reduced ? HERO_SCORE : 0}</span>
                <small style={{
                  fontFamily: TT.fontMono, fontSize: '0.15em', fontWeight: 500,
                  color: TT.textDim, letterSpacing: '0.1em', verticalAlign: 'super', marginLeft: 4,
                }}>/100</small>
              </span>
            </div>
            <div ref={verdictRef} className="hero-verdict" style={{
              fontFamily: TT.fontMono, fontSize: 11, letterSpacing: '0.1em', color: TT.green,
              marginTop: 14, textTransform: 'uppercase', opacity: reduced ? 1 : 0,
            }}>Specific. Constrained. No follow-up needed.</div>

            {/* eval --criteria: the reason behind the number */}
            <div ref={evalRef} className="hero-eval" style={{
              marginTop: 18, opacity: reduced ? 1 : 0,
            }}>
              <div style={{
                fontFamily: TT.fontMono, fontSize: 12, color: TT.textDim,
                fontStyle: 'italic', lineHeight: 1.6, marginBottom: 6,
              }}>{'// '}eval --criteria</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {HERO_CRITERIA.map((crit, i) => (
                  <div key={crit} className="hero-eval-row" style={{
                    display: 'flex', gap: 7, fontFamily: TT.fontMono, fontSize: 12,
                  }}>
                    <span
                      ref={(el) => { critRefs.current[i] = el; }}
                      style={{
                        flexShrink: 0, fontWeight: 700,
                        color: reduced ? TT.green : TT.textDim,
                      }}
                    >{reduced ? '✓' : '▢'}</span>
                    <span style={{ color: TT.textMuted }}>{crit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {!reduced && (
          <div ref={cueRef} aria-hidden="true" style={{
            position: 'absolute', bottom: 38, left: '50%', transform: 'translateX(-50%)',
            fontFamily: TT.fontMono, fontSize: '0.62rem', letterSpacing: '0.22em',
            color: TT.textDim, textTransform: 'uppercase', display: 'flex',
            flexDirection: 'column', alignItems: 'center', gap: 10, zIndex: 6,
          }}>
            <span style={{
              display: 'block', width: 1, height: 34,
              background: `linear-gradient(${TT.textDim}, transparent)`,
            }}/>
            Scroll to write it
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── Voice Room Demo ─── */
/* Fixed height so the demo reserves its footprint before the
   waveform canvas sizes itself. */
const DEMO_H = 460;

const VoiceRoomDemo = () => {
  /* Dark-only page: this component's remaining light branches are
     dead. Retokenised properly in the section pass; pinned here so the
     page has no light holes in the meantime. */
  const isDark = true;

  const [active, setActive] = useState(false);
  const [transcript, setTranscript] = useState('');
  const full = "Hey Marcus, our logistics AI saves midmarket teams 12 hours a week. Worth a 10-minute demo?";
  const timers = useRef([]);

  useEffect(() => {
    const cycle = () => {
      if (document.hidden) { const id = setTimeout(cycle, 500); timers.current.push(id); return; }
      setActive(true); setTranscript('');
      let i = 0;
      const id = setInterval(() => {
        if (document.hidden) return;
        i++; setTranscript(full.slice(0,i)); if (i>=full.length) clearInterval(id);
      }, 38);
      timers.current.push(id);
      const tid = setTimeout(() => { setActive(false); setTranscript(''); const nid = setTimeout(cycle, 2500); timers.current.push(nid); }, 7000);
      timers.current.push(tid);
    };
    const startId = setTimeout(cycle, 1200); timers.current.push(startId);
    return () => timers.current.forEach(id => { clearTimeout(id); clearInterval(id); });
  }, []);

  const dm=isDark, bg=dm?'#0c1829':'#f0f9ff', bdr=dm?'#1e3a5f':'#bae6fd';
  const cd=dm?'#0f172a':'#fff', tx=dm?'#e0f2fe':'#0c4a6e', mt=dm?'#64748b':'#0369a1';

  return (
    <div style={{ background:bg,border:`1px solid ${bdr}`,borderRadius:18,overflow:'hidden',
      boxShadow:dm?'0 32px 72px -16px rgba(0,0,0,0.6)':'0 32px 72px -16px rgba(2,132,199,0.16)',
      display:'flex',flexDirection:'column',height:DEMO_H,position:'relative' }}>
      {/* Header */}
      <div style={{ background:dm?'#0f172a':'#e0f2fe',borderBottom:`1px solid ${bdr}`,padding:'9px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,position:'relative',zIndex:2 }}>
        <div style={{ display:'flex',alignItems:'center',gap:7 }}>
          <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
            <rect x="0"  y="4"  width="2" height="6"  rx="1" fill="#0ea5e9"/>
            <rect x="3"  y="1"  width="2" height="12" rx="1" fill="#0ea5e9"/>
            <rect x="6"  y="3"  width="2" height="8"  rx="1" fill="#0ea5e9"/>
            <rect x="9"  y="0"  width="2" height="14" rx="1" fill="#0ea5e9"/>
            <rect x="12" y="3"  width="2" height="8"  rx="1" fill="#0ea5e9"/>
          </svg>
          <span style={{ fontSize:10,fontWeight:700,color:mt,letterSpacing:'0.05em' }}>VOICE ROOM</span>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:5 }}>
          <div style={{ width:5,height:5,borderRadius:'50%',background:'#22c55e',boxShadow:'0 0 6px #22c55e' }}/>
          <span style={{ fontSize:9,color:mt,fontWeight:600 }}>Active</span>
        </div>
      </div>

      <div style={{ padding:14,flex:1,display:'flex',flexDirection:'column',gap:10,position:'relative',zIndex:2,overflow:'hidden' }}>
        {/* Character card */}
        <div style={{ display:'flex',alignItems:'center',gap:10,background:cd,border:`1px solid ${bdr}`,borderRadius:11,padding:11,flexShrink:0 }}>
          <div style={{ width:38,height:38,borderRadius:'50%',background:'linear-gradient(135deg,#0ea5e9,#38bdf8)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:'#fff',fontSize:14,flexShrink:0 }}>MC</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12,fontWeight:700,color:tx }}>Marcus Chen</div>
            <div style={{ fontSize:10,color:mt }}>VP Operations · Skeptical · Intermediate</div>
          </div>
          <div style={{ fontSize:9,fontWeight:700,color:'#0ea5e9',background:dm?'#1e3a5f':'#e0f2fe',padding:'2px 7px',borderRadius:99 }}>LIVE</div>
        </div>

        {/* Chat */}
        <div style={{ flex:1,display:'flex',flexDirection:'column',gap:7,justifyContent:'flex-end',overflow:'hidden' }}>
          <div style={{ alignSelf:'flex-start',background:dm?'#1e3a5f':'#e0f2fe',padding:'9px 12px',borderRadius:'12px 12px 12px 3px',maxWidth:'85%' }}>
            <p style={{ fontSize:11,color:tx,lineHeight:1.5,margin:0 }}>Hi, this is Marcus. I'm heading into a meeting, what do you need?</p>
          </div>
          <div style={{ alignSelf:'flex-end',background:dm?'#14532d':'#dcfce7',padding:'9px 12px',borderRadius:'12px 12px 3px 12px',maxWidth:'85%',minHeight:50 }}>
            <p style={{ fontSize:11,color:dm?'#86efac':'#166534',lineHeight:1.5,margin:0 }}>
              {transcript||<span style={{ opacity:0.35 }}>Your response...</span>}
            </p>
          </div>
        </div>

        {/* Waveform */}
        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:3,padding:'8px 0',flexShrink:0 }}>
          {Array.from({length:16}).map((_,i)=>(
            <div key={i} style={{ width:3,borderRadius:99,background:active?'#22c55e':mt,
              height:active?6+Math.abs(Math.sin(i*0.7))*18:5,
              transition:'height 0.15s ease, background 0.15s ease',opacity:active?1:0.35 }}/>
          ))}
        </div>
        <div style={{ textAlign:'center',fontSize:9,color:mt,fontWeight:600,flexShrink:0 }}>
          {active?'Listening...':'Tap mic to respond'}
        </div>
      </div>
    </div>
  );
};

/* ─── User Dropdown ─── */
const UserDropdown = ({ user, profile, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // Same pattern as Navbar.jsx: name from the `profiles` row if present,
  // else fall back to email, never both at once.
  const displayName = profile?.full_name?.trim() || null;
  const initial = (displayName ? displayName[0] : user?.email?.[0] || '?').toUpperCase();

  const bdr  = C.rule;
  const bg   = C.surface;
  const head = C.ink;
  const mut  = C.graphite;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={() => setOpen(o=>!o)}
        style={{ width:34,height:34,borderRadius:'50%',background:C.orange,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:13 }}>
        {initial}
      </button>

      {open && (
        <div style={{ position:'absolute',top:'calc(100% + 8px)',right:0,background:bg,border:`1px solid ${bdr}`,borderRadius:R.panel,boxShadow:'0 16px 40px -8px rgba(0,0,0,0.7)',minWidth:180,zIndex:200,overflow:'hidden' }}>
          <div style={{ padding:'10px 14px',borderBottom:`1px solid ${bdr}` }}>
            <div style={{ ...TYPE.bodyS, fontWeight:700,color:head,marginBottom:1 }}>{displayName || 'Your account'}</div>
            {displayName && (
              <div style={{ ...TYPE.bodyS, color:mut,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{user?.email}</div>
            )}
          </div>
          {[
            { icon:LayoutDashboard, label:'Dashboard', to:'/dashboard' },
            { icon:BookOpen,        label:'Skills',    to:'/skills' },
            { icon:Settings,        label:'Settings',  to:'/settings' },
          ].map(({ icon:Icon, label, to }) => (
            <button key={to} onClick={() => { navigate(to); setOpen(false); track('dropdown_nav',{to}); }}
              style={{ width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'none',border:'none',cursor:'pointer',textAlign:'left',color:head,...TYPE.bodyS }}
              onMouseEnter={e=>{e.currentTarget.style.background=C.raised;}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
              <Icon size={14} color={mut}/>{label}
            </button>
          ))}
          <div style={{ borderTop:`1px solid ${bdr}` }}>
            <button onClick={() => { onSignOut?.(); setOpen(false); track('signout'); }}
              style={{ width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'none',border:'none',cursor:'pointer',textAlign:'left',color:C.red,...TYPE.bodyS }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(239,68,68,0.10)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
              <LogOut size={14} color={C.red}/>Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Header ───
   Sits on TT.bg with no visible seam at scroll 0, so the page opens
   as one unbroken black field continuous with the hero. The bottom
   hairline fades in across the first 80px of scroll, which is what
   separates "merged into the hero" from "a bar above the hero"
   without ever animating a layout property.

   The border is painted as a box-shadow rather than a real border so
   its opacity can be animated without the 1px of height appearing
   and disappearing underneath the header and nudging the page. */
const HEADER_MERGE_PX = 80;

const Header = ({ user, profile, loading, onSignOut }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [seam, setSeam] = useState(0);
  const tx = C.ink;

  useEffect(() => {
    let queued = false;
    const paint = () => {
      queued = false;
      setSeam(Math.min(1, Math.max(0, window.scrollY / HEADER_MERGE_PX)));
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    paint();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="landing-header" style={{
      position:'sticky', top:0, zIndex:50,
      background: C.bg,
      boxShadow: `inset 0 -1px 0 0 rgba(42,42,42,${seam})`,
    }}>
      <style>{`
        @keyframes header-auth-reveal { from { opacity:0; transform:scale(0.6); } to { opacity:1; transform:scale(1); } }
        .header-auth-reveal { animation: header-auth-reveal 300ms ease-out both; }
      `}</style>
      <div className="max-w-[1200px] mx-auto px-6" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',height:'var(--header-h)' }}>
        <Link to="/" style={{ display:'flex',alignItems:'center',gap:10,textDecoration:'none' }} onClick={() => track('logo_click')}>
          <CastleLogoMark size={26}/>
          <span style={{ ...TYPE.heading }}>
            <span style={{ color: C.ink }}>Castle </span>
            <span style={{ color: C.orange }}>Atlas</span>
          </span>
        </Link>

        <div className="hidden md:flex" style={{ alignItems:'center',gap:16 }}>
          {loading ? (
            // Invisible placeholder, sized to the avatar circle. Holds the
            // layout's shape so nothing shifts once the real content (avatar
            // or Login/Start free) pops in. No visible loading indicator.
            <div style={{ width:34, height:34, opacity:0 }} />
          ) : user ? (
            <div className="header-auth-reveal">
              <UserDropdown user={user} profile={profile} onSignOut={onSignOut}/>
            </div>
          ) : (
            <div className="header-auth-reveal" style={{ display:'flex',alignItems:'center',gap:16 }}>
              <Link
                to="/login"
                style={{ ...TYPE.bodyS,fontWeight:600,color:C.graphite,textDecoration:'none',transition:'color 0.15s' }}
                onMouseEnter={(e)=>{ e.currentTarget.style.color = C.ink; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.color = C.graphite; }}
                onClick={() => track('nav_login')}
              >Log in</Link>
              <Link to="/signup" style={{ background:C.orange,color:'#fff',...TYPE.bodyS,fontWeight:700,padding:'9px 18px',borderRadius:R.button,textDecoration:'none' }} onClick={() => track('nav_signup')}>Start free</Link>
            </div>
          )}
        </div>

        <button className="md:hidden" onClick={() => setMobileOpen(o=>!o)} style={{ background:'none',border:'none',color:tx,cursor:'pointer' }} aria-label="Menu">
          {mobileOpen?<X size={20}/>:<Menu size={20}/>}
        </button>
      </div>

      {mobileOpen && (
        <div style={{ padding:'12px 24px 20px',display:'flex',flexDirection:'column',gap:12,borderTop:`1px solid ${C.rule}`,background:C.bg }}>
          {loading ? (
            <div style={{ height:20, width:'60%', opacity:0 }} />
          ) : user ? (
            <div className="header-auth-reveal" style={{ display:'flex',flexDirection:'column',gap:12 }}>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} style={{ ...TYPE.body,fontWeight:600,color:tx,textDecoration:'none' }}>Dashboard</Link>
              <Link to="/skills"    onClick={() => setMobileOpen(false)} style={{ ...TYPE.body,fontWeight:600,color:tx,textDecoration:'none' }}>Skills</Link>
              <Link to="/settings"  onClick={() => setMobileOpen(false)} style={{ ...TYPE.body,fontWeight:600,color:tx,textDecoration:'none' }}>Settings</Link>
              <button onClick={() => { onSignOut?.(); setMobileOpen(false); }} style={{ textAlign:'left',background:'none',border:'none',...TYPE.body,fontWeight:600,color:'#ef4444',cursor:'pointer' }}>Sign out</button>
            </div>
          ) : (
            <div className="header-auth-reveal" style={{ display:'flex',gap:10 }}>
              <Link to="/login"  style={{ flex:1,textAlign:'center',padding:'10px 0',borderRadius:R.button,border:`1px solid ${C.rule}`,color:tx,...TYPE.bodyS,fontWeight:600,textDecoration:'none' }}>Log in</Link>
              <Link to="/signup" style={{ flex:1,textAlign:'center',padding:'10px 0',borderRadius:R.button,background:C.orange,color:'#fff',...TYPE.bodyS,fontWeight:700,textDecoration:'none' }}>Start free</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

/* ─── Notify Form ─── */
const NotifyForm = () => {
  /* Dark-only page: this component's remaining light branches are
     dead. Retokenised properly in the section pass; pinned here so the
     page has no light holes in the meantime. */
  const isDark = true;

  const [email, setEmail] = useState('');
  const [done, setDone]   = useState(false);
  const [loading, setL]   = useState(false);
  const [err, setErr]     = useState('');

  const submit = async e => {
    e.preventDefault(); if (!email) return;
    setL(true); setErr(''); track('notify_submit');
    try {
      const { supabase } = await import('../services/api.js');
      const { error } = await supabase.from('waitlist').insert({ email, source:'landing_v4' });
      if (error) throw error; setDone(true);
    } catch(e) { console.error(e); setErr('Something went wrong, please try again.'); }
    finally { setL(false); }
  };

  const bdr=isDark?'#3d3532':'#d4cdc7', ibg=isDark?'#292524':'#fff', col=isDark?'#fafaf9':'#1c1917';

  if (done) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'14px 18px',background:ibg,borderRadius:R.button,border:`2px solid ${C.green}`,width:'100%' }}>
      <CheckCircle size={17} color="#22c55e" strokeWidth={2.5}/>
      <span style={{ ...TYPE.bodyS,fontWeight:700,color:'#22c55e' }}>You're on the list.</span>
    </div>
  );

  return (
    <div style={{ width:'100%' }}>
      <form onSubmit={submit} style={{ display:'flex',gap:8,width:'100%' }}>
        <label htmlFor="ne" className="sr-only">Email</label>
        <input id="ne" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required
          style={{ padding:'0 15px',borderRadius:R.button,border:`1px solid ${bdr}`,background:ibg,color:col,...TYPE.bodyS,outline:'none',height:42,flex:1,minWidth:0,boxSizing:'border-box' }}/>
        <button type="submit" disabled={loading||!email}
          style={{ background:C.orange,color:'#fff',fontWeight:700,...TYPE.bodyS,padding:'0 18px',borderRadius:R.button,border:'none',cursor:loading||!email?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',opacity:loading||!email?0.6:1,height:42,flexShrink:0 }}>
          {loading?<div style={{ width:13,height:13,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite' }}/>:<><Bell size={14} strokeWidth={2.5}/> Notify me</>}
        </button>
      </form>
      {err&&<p style={{ ...TYPE.bodyS,color:'#ef4444',marginTop:5 }}>{err}</p>}
    </div>
  );
};

/* ─── FAQ ─── */
const FAQItem = ({ q, a, n }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:`1px solid ${C.rule}` }}>
      <button onClick={()=>{setOpen(o=>!o);track('faq_toggle',{q});}} aria-expanded={open}
        style={{ width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 0',textAlign:'left',background:'transparent',border:'none',cursor:'pointer',gap:12 }}>
        <span style={{ display:'flex',alignItems:'baseline',gap:14,minWidth:0 }}>
          <span style={{ ...TYPE.monoS,textTransform:'none',color:C.dim,flexShrink:0,fontVariantNumeric:'tabular-nums' }}>{String(n).padStart(2,'0')}</span>
          <span style={{ ...TYPE.bodyL,fontWeight:600,color:C.ink,lineHeight:1.4 }}>{q}</span>
        </span>
        <ChevronDown size={16} color={C.graphite} style={{ flexShrink:0,transform:open?'rotate(180deg)':'rotate(0)',transition:'transform 0.2s' }}/>
      </button>
      {open&&<div style={{ paddingBottom:20,paddingLeft:25,...TYPE.body,color:C.graphite,lineHeight:1.75,maxWidth:640 }}>{a}</div>}
    </div>
  );
};

/* ─── Section heading ─── */
/* ─── Tier 2: terminal reveal ────────────────────────────────
   The connective tissue between the three pinned stages. On first
   entry the section's mono label types itself character by
   character behind a block cursor, then the heading and body fade
   up 16px. It makes the whole page feel like the hero without
   taking a second of scroll away from the reader.

   Fires once, then unobserves. Under prefers-reduced-motion the
   label is rendered whole with no typing and no cursor, and the
   fade-up is skipped, so nothing is hidden from anyone. */
const TYPE_MS_PER_CHAR = 38;

const useTypedReveal = (text, reduced) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(0);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (reduced) { setEntered(true); setShown(text.length); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        setEntered(true);
        io.unobserve(e.target);
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, text.length]);

  useEffect(() => {
    if (!entered || reduced || shown >= text.length) return undefined;
    const id = setTimeout(() => setShown((n) => n + 1), TYPE_MS_PER_CHAR);
    return () => clearTimeout(id);
  }, [entered, reduced, shown, text.length]);

  const typing = entered && shown < text.length;
  return { ref, entered, visible: text.slice(0, shown), typing };
};

/* Section head. Replaces the old numbered `Plate` device: the
   PLATE 01-07 sequence implied the sections were an ordered
   series, which they are not, so the number carried no
   information. `n` is still accepted and ignored so the existing
   call sites keep working until they are cleaned up. */
const Plate = ({ label, title, sub, align = 'center', reduced = false }) => {
  const eyebrow = String(label ?? '').toUpperCase();
  const { ref, entered, visible, typing } = useTypedReveal(eyebrow, reduced);
  return (
    <div
      ref={ref}
      style={{
        textAlign: align, maxWidth: align === 'center' ? 680 : 560,
        margin: align === 'center' ? '0 auto 56px' : '0 0 56px',
      }}
    >
      <div style={{
        ...TYPE.label,
        color: C.orange, marginBottom: 16,
        minHeight: '1.2em',
      }}>
        {visible}
        {typing && (
          <span aria-hidden="true" style={{
            display: 'inline-block', width: '0.55em', height: '1em',
            background: C.orange, verticalAlign: '-0.13em', marginLeft: 2,
          }}/>
        )}
      </div>
      <h2 style={{
        ...TYPE.displayM,
        color: C.ink,
        marginBottom: sub ? 16 : 0, textWrap: 'balance',
        opacity: entered ? 1 : 0,
        transform: entered ? 'none' : 'translateY(16px)',
        transition: reduced ? 'none' : 'opacity 500ms ease, transform 500ms ease',
      }}>{title}</h2>
      {sub && (
        <p style={{
          ...TYPE.bodyL, color: C.graphite, maxWidth: 560,
          margin: align === 'center' ? '0 auto' : '0',
          opacity: entered ? 1 : 0,
          transform: entered ? 'none' : 'translateY(16px)',
          transition: reduced ? 'none' : 'opacity 500ms ease 90ms, transform 500ms ease 90ms',
        }}>{sub}</p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ONE REP, SHOWN (Phase 4)
   ═══════════════════════════════════════════════════════════
   A single static worked example — brief, attempt, response, score
   with its criteria — using AI Prompting. Terminal styling (TT tokens)
   is scoped to this one card on purpose: the spec's explicit carve-out
   is that this is where it's allowed, because the card is demonstrating
   that specific skill's mechanic, not decorating the hub the way the
   old scroll hero did. Static, not scrubbed — no scroll, no typing
   animation, no counting-up score, and no invented perfect score: a
   genuine 54 with one unmet criterion is closer to what "a number
   comes back with the reason why" actually looks like on a first try. */
const ONE_REP = {
  brief: 'Write a short, polite email to my manager Sarah asking for this Friday off. Confirm every task on my plate will be done by Thursday EOD.',
  attempt: "Hey Sarah, can I take Friday off? I'll make sure everything is handled before then.",
  verdict: '"Everything" and "before then" are not commitments Sarah can hold you to. A stronger attempt names the day and states exactly what happens to each open task.',
  score: 54,
  criteria: [
    { label: 'Names the recipient and the exact day', met: true },
    { label: 'States what happens to the open work', met: false },
    { label: 'Constrains tone and length', met: true },
  ],
};

const OneRepShown = () => {
  const scoreColor = ONE_REP.score >= 80 ? C.orange : ONE_REP.score >= 60 ? TT.amber : '#ef4444';
  return (
    <section className="px-6 py-20 md:py-28 reveal">
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Plate label="One rep, shown" title="What a rep actually looks like"
          sub="No scrubbing, no scroll. One real exercise from AI Prompting, worked and scored." />
        <div style={{ background: TT.bgSurface, border: `1px solid ${TT.border}`, borderRadius: R.term, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${TT.border}` }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }}/>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }}/>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }}/>
            <span style={{ ...TYPE.monoS, color: TT.textDim, marginLeft: 8 }}>rep start --lesson 01</span>
          </div>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ ...TYPE.label, color: TT.orange, marginBottom: 8 }}>Brief</div>
              <p style={{ ...TYPE.body, color: TT.text, margin: 0 }}>{ONE_REP.brief}</p>
            </div>
            <div>
              <div style={{ ...TYPE.label, color: TT.orange, marginBottom: 8 }}>Your attempt</div>
              <p style={{ ...TYPE.body, fontFamily: 'var(--font-mono)', color: TT.text, margin: 0 }}>{ONE_REP.attempt}</p>
            </div>
            <div>
              <div style={{ ...TYPE.label, color: TT.orange, marginBottom: 8 }}>Why it scored this way</div>
              <p style={{ ...TYPE.body, color: TT.textMuted, margin: 0 }}>{ONE_REP.verdict}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, paddingTop: 16, borderTop: `1px solid ${TT.border}`, flexWrap: 'wrap' }}>
              <span style={{ ...TYPE.displayM, color: scoreColor, fontVariantNumeric: 'tabular-nums' }}>{ONE_REP.score}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ONE_REP.criteria.map((c) => (
                  <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, ...TYPE.bodyS, color: c.met ? TT.green : TT.textMuted }}>
                    <span>{c.met ? '✓' : '▢'}</span>{c.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Completion State (exported) ─── */
export const CompletionState = ({ totalReps=15, avgScore=0, onRetake }) => {
  /* Dark-only page: this component's remaining light branches are
     dead. Retokenised properly in the section pass; pinned here so the
     page has no light holes in the meantime. */
  const isDark = true;

  const [score, setScore] = useState(0);
  useEffect(() => { let s=0; const id=setInterval(()=>{ s=Math.min(s+1,avgScore); setScore(s); if(s>=avgScore)clearInterval(id); },22); return ()=>clearInterval(id); }, [avgScore]);
  const head=isDark?'#fafaf9':'#1c1917', muted=isDark?'#a8a29e':'#78716c', card=isDark?'#1c1917':'#fff', bdr=isDark?'#2d2825':'#e8e4df';
  const tier=avgScore>=90?{label:'Elite',color:'#f59e0b',emoji:'🏆'}:avgScore>=75?{label:'Proficient',color:'#22c55e',emoji:'⚡'}:{label:'Apprentice',color:'#0ea5e9',emoji:'📈'};
  return (
    <div style={{ background:isDark?'linear-gradient(135deg,#1c1410,#231810)':'linear-gradient(135deg,#fff7ed,#ffedd5)',border:`1.5px solid ${isDark?'#5c3d1e':'#fed7aa'}`,borderRadius:24,padding:'36px 32px',textAlign:'center',position:'relative',overflow:'hidden' }}>
      <div style={{ position:'relative',zIndex:2 }}>
        <div style={{ fontSize:52,marginBottom:12 }}>{tier.emoji}</div>
        <div style={{ fontSize:11,fontWeight:800,color:tier.color,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:6 }}>Phase 1 Complete</div>
        <h2 style={{ fontSize:'clamp(1.4rem,4vw,2rem)',fontWeight:900,color:head,letterSpacing:'-0.03em',marginBottom:8,lineHeight:1.2 }}>You earned <span style={{ color:tier.color }}>{tier.label}</span> rank</h2>
        <p style={{ fontSize:13,color:muted,lineHeight:1.65,maxWidth:380,margin:'0 auto 28px' }}>You completed all {totalReps} reps in Phase 1.</p>
        <div style={{ display:'flex',justifyContent:'center',gap:16,marginBottom:28,flexWrap:'wrap' }}>
          {[{label:'Avg Score',value:`${score}/100`,color:tier.color},{label:'Reps Done',value:totalReps,color:'#ea580c'},{label:'Rank',value:tier.label,color:tier.color}].map(({label,value,color})=>(
            <div key={label} style={{ background:card,border:`1px solid ${bdr}`,borderRadius:14,padding:'14px 20px',minWidth:90 }}>
              <div style={{ fontSize:10,fontWeight:700,color:muted,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:4 }}>{label}</div>
              <div style={{ fontSize:20,fontWeight:900,color }}>{value}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap' }}>
          <Link to="/skills" style={{ display:'inline-flex',alignItems:'center',gap:6,background:'#ea580c',color:'#fff',fontWeight:700,fontSize:13,padding:'11px 22px',borderRadius:11,textDecoration:'none' }} onClick={() => track('completion_next')}>Start next skill <ArrowRight size={15}/></Link>
          <button onClick={() => { track('completion_retake'); onRetake?.(); }} style={{ display:'inline-flex',alignItems:'center',gap:6,background:'transparent',color:muted,fontWeight:600,fontSize:13,padding:'11px 22px',borderRadius:11,border:`1px solid ${bdr}`,cursor:'pointer' }}><RotateCcw size={13}/> Retake Phase 1</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   THE OPPOSITION: sliding marquee
   ═══════════════════════════════════════════════════════════
   Back to a two-row carousel running in opposite directions.
   The motion and the difficulty colours were both carrying
   weight that the static assembling grid threw away. It is
   normal flow with a tier-2 reveal rather than a pinned stage,
   which leaves the page with two pins instead of three.

   What it keeps from the grid: the cast is derived from
   ALL_CHARACTERS at module load, never hardcoded, and a real
   portrait renders wherever `avatar` resolved. The rest fall
   back to initials, so the rows stay full while art lands.

   Difficulty colour is the one place on this page where a
   second hue earns its keep, because it encodes information
   rather than decorating.
   ═══════════════════════════════════════════════════════════ */
const DIFF_STYLE = {
  beginner:     { color: '#0ea5e9', label: 'Beginner' },
  intermediate: { color: '#f59e0b', label: 'Intermediate' },
  advanced:     { color: '#ef4444', label: 'Advanced' },
};

const CAST = Object.values(ALL_CHARACTERS).filter((ch) => isScenarioLive(ch.category)).map((ch) => ({
  id: ch.id, name: ch.name, role: ch.role,
  avatar: ch.avatar ?? null, diff: ch.difficulty,
}));
const CAST_TOTAL = CHARACTER_COUNTS.live;
const CAST_WITH_ART = CAST.filter((c) => c.avatar).length;

/* The marquee duplicates whatever it is handed so the loop can wrap,
   so feeding it all 160 would mount 320 chips. Characters with art
   sort first, then the row is capped. */
const MARQUEE_LEN = 44;
const MARQUEE_CAST = [...CAST]
  .sort((a, b) => (b.avatar ? 1 : 0) - (a.avatar ? 1 : 0))
  .slice(0, MARQUEE_LEN);

const OpponentChip = ({ o }) => {
  // A null avatar never fires onError, so it is branched on below; a path that
  // 404s falls back to initials too, same as VoiceRoom's CharacterPortrait.
  const [imgFailed, setImgFailed] = useState(false);
  const d = DIFF_STYLE[o.diff] ?? DIFF_STYLE.beginner;
  const initials = o.name.replace('Dr. ', '').split(' ').map((w) => w[0]).slice(0, 2).join('');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      background: TT.bgSurface, border: `1px solid ${TT.border}`,
      borderRadius: 16, padding: '12px 20px 12px 12px',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        background: `${d.color}18`, border: `1.5px solid ${d.color}45`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14, color: d.color,
      }}>
        {o.avatar && !imgFailed
          ? (
            <img
              src={o.avatar} alt="" loading="lazy" width="42" height="42"
              onError={() => setImgFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )
          : initials}
      </div>
      <div>
        <div style={{ ...TYPE.bodyS, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap' }}>{o.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
          <span style={{ ...TYPE.bodyS, color: C.graphite }}>{o.role}</span>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: d.color, flexShrink: 0 }}/>
          <span style={{ ...TYPE.label, color: d.color, letterSpacing: '0.04em' }}>{d.label}</span>
        </div>
      </div>
    </div>
  );
};

const OpponentMarquee = () => {
  const rowA = MARQUEE_CAST.filter((_, i) => i % 2 === 0);
  const rowB = MARQUEE_CAST.filter((_, i) => i % 2 === 1);
  return (
    <div style={{ position: 'relative', overflow: 'hidden', padding: '8px 0' }}>
      {[{ row: rowA, cls: 'marq-l' }, { row: rowB, cls: 'marq-r' }].map(({ row, cls }) => (
        <div
          key={cls} className={cls}
          style={{ display: 'flex', gap: 14, marginBottom: 14, width: 'max-content' }}
        >
          {[...row, ...row].map((o, i) => <OpponentChip key={`${o.id}-${i}`} o={o}/>)}
        </div>
      ))}
      <div aria-hidden="true" style={{ position: 'absolute', inset: '0 auto 0 0', width: 90, background: `linear-gradient(90deg, ${C.bg}, transparent)`, pointerEvents: 'none' }}/>
      <div aria-hidden="true" style={{ position: 'absolute', inset: '0 0 0 auto', width: 90, background: `linear-gradient(270deg, ${C.bg}, transparent)`, pointerEvents: 'none' }}/>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TERRITORIES  [PIN 2 of 2]
   ═══════════════════════════════════════════════════════════
   These cards are NOT reimplemented here. Landing.jsx used to
   hand-draw an approximation of each skill's material, which
   was always going to be a lookalike and would drift the
   moment a skill page changed. It now mounts the very same
   components the Skills page mounts, through the same
   registry, so each skill's identity has exactly one source:

     terminal -> AiPromptingCard          traffic lights, typing, dot trail
     contract -> SalesNegotiationCard     aged paper, signature, script CTA
     pencil   -> CopywritingCard          legal pad, sketched frame
     cloud    -> PrecisionStorytellingCard  Quill, glass panel, cloud wash

   Technical Translation is deliberately absent from that list:
   it is a different card TYPE, not a shape in the registry, and
   is rendered by VoteForNextSection, which owns TranslatorSkillCard
   and the vote wiring.

   Rows come from the Supabase `skills` table via useSkills(), the
   same fetch Skills.jsx uses, which is what finally retires the
   hardcoded lesson and rep counts on this page.
   ═══════════════════════════════════════════════════════════ */
const TerritorySection = ({ reduced, isDark }) => {
  const stageRef = useRef(null);
  const cardRefs = useRef([]);
  const navigate = useNavigate();
  const { skills, loading, error } = useSkills();

  /* Skills.jsx passes neither onClick nor onAction, letting each config's
     own internal link do the navigating. Here we DO pass them, because the
     landing page has to keep firing skill_cta for PostHog. Both routes go
     through the same handler so the event fires whether the visitor clicks
     the card body or its CTA. */
  const goToSkill = (sk, via) => {
    track('skill_cta', { skill: sk.name, via });
    const to = sk.landing_route || sk.route;
    if (to) navigate(to);
  };
  const wideEnoughToPin = useMediaQuery('(min-width: 1320px)');
  /* `flat` means: render as an ordinary section in normal flow. */
  const flat = reduced || !wideEnoughToPin;

  const liveCards = skills
    .filter((sk) => sk.status === 'live')
    .map((sk) => ({ skill: sk, Card: getSkillCard(sk.card_shape) }))
    .filter((x) => x.Card);

  useEffect(() => {
    if (flat) {
      cardRefs.current.forEach((el) => {
        if (el) { el.style.opacity = '1'; el.style.transform = 'none'; }
      });
      return undefined;
    }
    const stage = stageRef.current;
    if (!stage) return undefined;
    let queued = false;
    const paint = () => {
      queued = false;
      const rect = stage.getBoundingClientRect();
      const travel = stage.offsetHeight - window.innerHeight;
      const p = travel > 0 ? Math.max(0, Math.min(1, -rect.top / travel)) : 0;
      const build = seg(p, 0.06, 0.72);
      const n = cardRefs.current.length || 1;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const startAt = (i / n) * 0.86;
        const t = easeInOut(seg(build, startAt, startAt + 0.2));
        el.style.opacity = String(t);
        el.style.transform = `translateY(${(1 - t) * 18}px) scale(${0.96 + t * 0.04})`;
      });
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    paint();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    /* liveCards.length matters here: the refs array only exists once
       the Supabase rows have landed, so the first paint has to re-run. */
  }, [flat, liveCards.length]);

  return (
    <section
      id="skills"
      ref={stageRef}
      className={flat ? 'terr-stage reveal' : 'terr-stage'}
      style={{ position: 'relative', height: flat ? 'auto' : 'var(--terr-h)' }}
    >
      <div style={{
        position: flat ? 'static' : 'sticky', top: 0,
        height: flat ? 'auto' : '100vh',
        boxSizing: 'border-box',
        padding: flat ? '80px 0' : 'calc(var(--header-h) + 24px) 0 24px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div className="px-6 terr-head" style={{ maxWidth: 1240, margin: '0 auto', width: '100%' }}>
          <Plate
            reduced={reduced}
            label="The map"
            title="Pick your first territory"
            sub="Every skill is its own world, with its own drills and its own opponents, all scored by one engine."
          />
          {loading ? (
            <div style={{
              ...TYPE.monoS, color: C.dim,
              textAlign: 'center', padding: '40px 0',
            }}>loading skills_</div>
          ) : error ? (
            /* useSkills() surfaces fetch failures as `error`, but until now
               nothing here read it: a failed fetch silently fell through to
               an empty .terr-grid with the heading still showing above it —
               "the section renders empty," with no signal of why. */
            <div style={{
              ...TYPE.monoS, color: C.dim,
              textAlign: 'center', padding: '40px 0',
            }}>
              {error} <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'none', border: 'none', color: C.orange,
                  textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0,
                }}
              >Reload</button>
            </div>
          ) : (
            <div className="terr-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,20px)' }}>
              {liveCards.map(({ skill: sk, Card }, i) => (
                <div
                  key={sk.id}
                  ref={(el) => { cardRefs.current[i] = el; }}
                  style={{ opacity: 0, display: 'flex', willChange: 'opacity, transform' }}
                >
                  <Card
                    skill={sk}
                    isDark={isDark}
                    mode="skills"
                    onClick={() => goToSkill(sk, 'card')}
                    onAction={() => goToSkill(sk, 'cta')}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════
   MAIN LANDING
   One continuous terminal session, header to footer
═══════════════════════════════════════ */
const Landing = () => {
  const { user, profile, loading, signOut } = useAuth();
  useScrollReveal();
  useSchema();
  /* Called, not read. This page is one continuous terminal session and
     is dark in both modes, so nothing here branches on the theme. The
     hook stays wired because it is still the mechanism the rest of the
     app uses, and because a future section that genuinely needs a light
     variant should be able to reach for it without re-plumbing. */
  useDarkMode();
  const reduced = useReducedMotion();

  /* Paint html/body/#root the terminal background for as long as this
     route is mounted. Without it, overscroll bounce and any viewport
     taller than the content reveal the app's light --background token
     behind this page. Removed on unmount so no other route inherits it. */
  useEffect(() => {
    document.documentElement.classList.add('landing-terminal');
    return () => document.documentElement.classList.remove('landing-terminal');
  }, []);

  /* Page chrome deliberately ignores isDark: this page is dark in both
     modes. These aliases exist so the not-yet-migrated sections below
     keep reading from one place. */
  const head  = C.ink;
  const muted = C.graphite;
  const card  = C.surface;
  const cardB = C.rule;

  return (
    /* overflow-x MUST be `clip`, not `hidden`.
       `hidden` makes this element a scroll container, and a scroll
       container is what `position:sticky` resolves against, so all
       three pinned stages would stick to a box the full height of the
       document and therefore never actually pin. `clip` gives the same
       horizontal-overflow protection without creating that container.
       This is the whole reason the hero pin appeared to do nothing. */
    <div id="main-content" tabIndex={-1} style={{ fontFamily:'var(--font-body)', background:C.bg, color:C.ink, overflowX:'clip' }} className="min-h-screen">
      <style>{`
        /* Pinned stage heights. Kept in CSS rather than inline so the
           mobile value comes from a media query instead of a JS width
           read, which would otherwise run before hydration and cause
           the stage to resize under the user on first paint. */
        /* header height, shared by the header itself and by every
           pinned stage that has to centre its content below it */
        :root { --header-h: 64px; }
        .story-stage { --stage-h: 460vh; }
        @media (max-width: 640px) { .story-stage { --stage-h: 380vh; } }
        /* PIN 2: character wall.
           The whole cast has to fit inside one pinned viewport, so the
           column count is chosen to keep the ROW count low rather than
           to keep tiles large: 32 tiles across 11 columns is 3 rows,
           across 8 is 4. Below the point where a tile is too narrow to
           carry a name, the labels come off and it becomes a wall of
           faces, which is the same proof told more densely. */
        .wall-stage { --wall-h: 280vh; }
        .wall-head h2 { font-size: clamp(1.4rem, 3vw, 2.1rem) !important; }
        .wall-head p  { font-size: 14px !important; }
        .wall-head > div { margin-bottom: 26px !important; }
        .wall-grid { grid-template-columns: repeat(11, minmax(0, 1fr)); }
        @media (max-width: 1100px) { .wall-grid { grid-template-columns: repeat(8, minmax(0, 1fr)); } }
        @media (max-width: 860px)  { .wall-grid { grid-template-columns: repeat(8, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .wall-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); } }
        /* labels off once a tile is too narrow to read them */
        @media (max-width: 860px) {
          .wall-tile-meta { display: none; }
          .wall-grid { gap: 8px; }
        }
        @media (max-height: 800px) and (min-width: 861px) {
          .wall-head h2 { font-size: clamp(1.2rem, 2.4vw, 1.7rem) !important; }
          .wall-head p  { font-size: 13px !important; }
          .wall-head > div { margin-bottom: 14px !important; }
          .wall-count { margin-top: 12px !important; }
        }
        @media (max-height: 700px) {
          .wall-head p { display: none; }
          .wall-tile-meta { display: none; }
        }
        /* PIN 2: territories.
           Four columns, because there are exactly four live skills and
           these are the REAL card components: they are not interchangeable
           boxes that can be reflowed to taste, they each have their own
           intrinsic height (the storytelling card carries Quill at 470px).
           Wrapping to a second row put the tallest card half outside the
           pin, so the grid adapts to the cards rather than the reverse. */
        .terr-stage { --terr-h: 260vh; }
        .terr-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); align-items: start; }
        @media (max-width: 1320px) { .terr-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 700px)  { .terr-grid { grid-template-columns: 1fr; } }
        @media (max-height: 860px) {
          .terr-head h2 { font-size: clamp(1.3rem, 2.6vw, 1.9rem) !important; }
          .terr-head p  { font-size: 13.5px !important; }
          .terr-head > div { margin-bottom: 18px !important; }
        }
        /* Short-viewport compaction. The pinned stage is exactly 100vh,
           so on a short phone the terminal was taller than its own pin
           and lost the chrome bar off the top and the verdict off the
           bottom. These are height queries, not width queries, because
           the constraint is vertical: a 390x664 phone needs this and a
           390x900 one does not. Everything shrinks proportionally so
           the score row, which is the payoff of the whole sequence,
           always lands inside the fold. */
        @media (max-height: 844px) {
          .hero-term-body { padding: 16px 18px !important; }
          .hero-cmd { margin-bottom: 10px !important; font-size: 12px !important; }
          .hero-brief { margin-bottom: 14px !important; font-size: 13px !important; }
          .hero-slot { margin-bottom: 12px !important; }
          .hero-pane-prompt { min-height: 3.4em !important; }
          .hero-pane-resp { min-height: 4.2em !important; }
          .hero-score-row { padding-top: 14px !important; }
          .hero-score-num { font-size: clamp(2.4rem, 7vw, 3.6rem) !important; }
          .hero-eval { margin-top: 12px !important; }
        }
        @media (max-height: 764px) {
          .hero-term-body { padding: 12px 16px !important; }
          .hero-cmd { margin-bottom: 8px !important; }
          .hero-brief { margin-bottom: 10px !important; font-size: 12.5px !important; }
          .hero-slot { margin-bottom: 8px !important; }
          .hero-pane-prompt { min-height: 2.6em !important; }
          .hero-pane-resp { min-height: 3.1em !important; }
          .hero-score-row { padding-top: 10px !important; }
          .hero-score-num { font-size: clamp(2rem, 6vw, 2.8rem) !important; }
          .hero-eval { margin-top: 10px !important; }
          .hero-eval > div:first-child { margin-bottom: 4px !important; }
          /* On a short viewport the panes are sized by their CONTENT, not
             by min-height: the sample response carries hard newlines
             (subject, greeting, body, sign-off) so it is ~9 lines no
             matter how narrow the column gets. Tightening leading is the
             only lever that actually reclaims height here. */
          .hero-pane { font-size: 11.5px !important; line-height: 1.42 !important; }
        }
        /* Narrow AND short together is the worst case: the column is too
           narrow to fit the sample text on few lines, and there is no
           height to spend on the lines it does need. Small phones in
           portrait land here. */
        @media (max-height: 764px) and (max-width: 380px) {
          .hero-brief { display: none; }
          .hero-pane { font-size: 10.5px !important; line-height: 1.28 !important; }
          .hero-term-body { padding: 10px 13px !important; }
        }
        /* 320x568 (the smallest phone still in the wild) needs one more
           notch, and the chrome bar's URL is the least load-bearing
           thing left to drop. */
        @media (max-height: 684px) and (max-width: 340px) {
          .hero-pane { font-size: 10px !important; line-height: 1.22 !important; }
          .hero-cmd { font-size: 11px !important; margin-bottom: 6px !important; }
          .hero-chrome-path { display: none; }
        }
        /* Under ~600px of viewport there is less than 540px of usable
           height once the header is reserved, which is shorter than the
           sample email is tall. The response keeps its hard newlines
           (it has to still read as an email) so the remaining budget
           comes out of leading, the score numeral, and the slot labels,
           which are the only things here that are decoration rather
           than content. */
        @media (max-height: 600px) {
          .hero-slot-label { display: none; }
          .hero-pane { font-size: 9.5px !important; line-height: 1.15 !important; }
          .hero-slot { margin-bottom: 6px !important; }
          .hero-score-num { font-size: 1.4rem !important; }
          .hero-score-row { padding-top: 8px !important; }
          .hero-verdict { font-size: 9px !important; margin-top: 8px !important; }
          .hero-eval { margin-top: 6px !important; }
          .hero-eval-row { font-size: 10px !important; }
          .hero-term-body { padding: 8px 12px !important; }
        }
        /* Below this the brief is the first thing to go: the prompt,
           the response and the score are the sequence, the brief is
           context the reader can infer from the prompt itself. */
        @media (max-height: 684px) {
          .hero-brief { display: none; }
          .hero-term-body { padding: 10px 14px !important; }
          .hero-pane-prompt { min-height: 2em !important; }
          .hero-pane-resp { min-height: 2.2em !important; }
          .hero-score-num { font-size: 1.7rem !important; }
          .hero-pane { font-size: 11px !important; line-height: 1.3 !important; }
          /* the eval --criteria caption goes before the criteria
             themselves do: the tick rows carry the meaning, the
             caption is only naming what they are */
          .hero-eval > div:first-child { display: none; }
          .hero-eval { margin-top: 8px !important; }
        }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
        @keyframes marqL { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes marqR { from{transform:translateX(-50%)} to{transform:translateX(0)} }
        .marq-l{animation:marqL 55s linear infinite}
        .marq-r{animation:marqR 62s linear infinite}
        .marq-l:hover,.marq-r:hover{animation-play-state:paused}
        .reveal{opacity:0;transform:translateY(16px);transition:opacity 0.5s ease,transform 0.5s ease;}
        .revealed{opacity:1!important;transform:translateY(0)!important;}
        .reveal-1{transition-delay:0.05s}.reveal-2{transition-delay:0.13s}.reveal-3{transition-delay:0.21s}
        .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;}
        .hf{animation:fadeUp 0.6s ease both}.hf2{animation:fadeUp 0.6s ease 0.1s both}.hf3{animation:fadeUp 0.6s ease 0.2s both}.hf4{animation:fadeUp 0.6s ease 0.3s both}
        @media(prefers-reduced-motion:reduce){.reveal,.hf,.hf2,.hf3,.hf4,.marq-l,.marq-r{transition:none!important;animation:none!important;opacity:1!important;transform:none!important;}}
      `}</style>

      <Header user={user} profile={profile} loading={loading} onSignOut={signOut}/>

      {/* ── HERO: scroll-driven rep [PIN 1] ── */}
      <ScrollRepHero reduced={reduced}/>
      <OneRepShown/>

      {/* ── FIGURES: every number computed from the content modules ── */}
      <section className="px-6 py-20 md:py-24 reveal">
        <div className="max-w-[1100px] mx-auto">
          <div style={{ borderTop:`1px solid ${cardB}`,borderBottom:`1px solid ${cardB}` }}
            className="grid grid-cols-2 md:grid-cols-4">
            {FIGURES.map(({ value,label,sub }, i) => (
              <div key={label} style={{
                padding:'40px 28px',
                borderLeft: i % 4 === 0 ? 'none' : `1px solid ${cardB}`,
              }}>
                <div style={{ ...TYPE.displayL,color:head,lineHeight:1,marginBottom:10,fontVariantNumeric:'tabular-nums' }}>{value}</div>
                <div style={{ ...TYPE.label,color:C.orange,marginBottom:6 }}>{label}</div>
                <div style={{ ...TYPE.bodyS,color:muted }}>{sub}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign:'center',...TYPE.label,color:C.dim,marginTop:18 }}>
            Every figure here is countable inside the product. We don&rsquo;t publish numbers we can&rsquo;t show you.
          </p>
        </div>
      </section>

      {/* ── THE 85% ── */}
      <section className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[1100px] mx-auto grid gap-10 md:gap-14 md:grid-cols-2 items-center">
          {/* The plate illustration is optional: if the file has not
              landed yet the section renders as a single column rather
              than shipping a broken image. */}
          {HAS_UNFINISHED_PLATE && (
            <div style={{
              background:C.surface, border:`1px solid ${C.rule}`, borderRadius:R.panel,
              padding:14, alignSelf:'start',
            }}>
              <img
                src={UNFINISHED_PLATE} alt="" loading="lazy"
                style={{ width:'100%', height:'auto', display:'block', borderRadius:R.inner }}
              />
            </div>
          )}
          <div style={{ gridColumn: HAS_UNFINISHED_PLATE ? 'auto' : '1 / -1', maxWidth: HAS_UNFINISHED_PLATE ? 'none' : 720, marginInline: HAS_UNFINISHED_PLATE ? 0 : 'auto' }}>
            <Plate reduced={reduced} align={HAS_UNFINISHED_PLATE ? 'left' : 'center'}
              label="The 85%" title="Watching is comfortable. That is the problem."/>
            <p style={{ ...TYPE.bodyL,color:muted,marginBottom:18 }}>
              Watching is comfortable, so you keep doing it, and comfort feels like progress
              until the day you have to perform. Then you find out that you recognise the
              material but you can&rsquo;t produce it. Reading about cold calls is not a cold
              call. Reading about prompting is not a prompt that worked.
            </p>
            <p style={{ ...TYPE.bodyL,color:muted }}>
              The fix isn&rsquo;t more information. Information is free and there&rsquo;s
              already too much of it. The fix is doing the thing badly, being told exactly
              how badly, and doing it again.
            </p>
          </div>
        </div>
      </section>

      {/* ── THE OPPOSITION: marquee, normal flow ── */}
      <section className="py-20 md:py-28 reveal" style={{ overflow: 'hidden' }}>
        <div className="px-6">
          <Plate
            reduced={reduced}
            label="Known inhabitants"
            title={`${CAST_TOTAL} people who will hang up on you.`}
            sub="Pick a prospect, tap the mic, pitch out loud. They interrupt, they object, and if you go quiet for long enough they end the call. No manager listening in, no colleague pretending to be a buyer."
          />
        </div>
        <OpponentMarquee/>
        <div className="px-6" style={{ textAlign: 'center', marginTop: 24 }}>
          <span style={{
            ...TYPE.label, color: C.dim,
          }}>
            Showing {CAST_WITH_ART} of {CAST_TOTAL} portraits · {CAST_TOTAL - CAST_WITH_ART} in production
          </span>
        </div>
      </section>

      {/* ── TERRITORIES [PIN 2] ── */}
      <TerritorySection reduced={reduced} isDark/>

      {/* ── VOICE ROOM ── */}
      <section id="voice" className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[720px] mx-auto">
          <Plate reduced={reduced} label="Live terrain" title="Practice as many conversations as you want."
            sub="Pick a prospect, tap the mic, and pitch out loud. The same scoring engine that reads your writing listens to the call."/>
          <VoiceRoomDemo/>
        </div>
      </section>

      {/* ── STRAIGHT ANSWERS ── */}
      <section className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[880px] mx-auto">
          <Plate reduced={reduced} label="Straight answers" title="No fake testimonials on this page"
            sub="Castle Atlas launched in 2026 and we're not going to invent happy customers. Here's the comparison instead, on things you can check yourself in one free session."/>
          <div style={{ overflowX:'auto',WebkitOverflowScrolling:'touch' }}>
            <div style={{ background:'transparent',border:`1px solid ${cardB}`,borderRadius:R.panel,overflow:'hidden',position:'relative',minWidth:560 }}>
              <div style={{ display:'grid',gridTemplateColumns:'1.2fr 1.4fr 1.4fr',padding:'14px 24px',background:C.raised,borderBottom:`1px solid ${cardB}` }}>
                <span/>
                <span style={{ ...TYPE.label,fontWeight:700,color:C.orange,letterSpacing:'0.08em' }}>Castle Atlas</span>
                <span style={{ ...TYPE.label,color:muted,letterSpacing:'0.08em' }}>Tutorials / Courses</span>
              </div>
              {[
                ['Learning style', 'Active scored reps',                    'Passive video watching'],
                ['Feedback',       'A number and the reason behind it',     'A comments section, maybe'],
                ['Coverage',       'One scoring engine across every skill', 'A separate course per skill'],
                ['Voice practice', `${CHARACTERS_TOTAL} characters, real speech recognition`, 'Rehearsing with your mirror'],
                ['Time to value',  'About 45 seconds for the first rep',    'An eight hour runtime'],
                ['Cost to start',  'Free',                                  '$49 to $199 upfront'],
              ].map(([label,atlas,other],i,arr) => (
                <div key={label} style={{ display:'grid',gridTemplateColumns:'1.2fr 1.4fr 1.4fr',padding:'16px 24px',borderBottom:i<arr.length-1?`1px solid ${cardB}`:'none',alignItems:'center',position:'relative',zIndex:2 }}>
                  <span style={{ ...TYPE.bodyS,fontWeight:600,color:muted }}>{label}</span>
                  <span style={{ ...TYPE.bodyS,fontWeight:700,color:C.orange }}>{atlas}</span>
                  <span style={{ ...TYPE.bodyS,color:muted }}>{other}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[1000px] mx-auto">
          <Plate reduced={reduced} label="Provisions" title="Transparent. No hidden paywalls."
            sub="Start free. Upgrade when you're ready."/>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {[
              {
                name:'Free', price:'$0', period:'/mo',
                desc:'Build the foundation. No card needed.',
                features:['AI Prompt Engineering, Phase 1','Scored exercises with written feedback','Streak tracking'],
                cta:'Start Free', link:'/signup', featured:false,
              },
              {
                name:'Explorer', price:'$15', period:'/mo',
                desc:'Build multiple skills at your own pace.',
                features:['Choose any 3 skills','All phases and lessons','AI feedback and scoring','Priority support'],
                cta:'Coming Soon', link:'#notify-section', featured:true, soon:true,
              },
              {
                name:'Pro', price:'$50', period:'/mo',
                desc:'Full access. No limits.',
                features:['Every skill, as it ships','Unlimited Voice Room','Early access to new skills','Priority support'],
                cta:'Coming Soon', link:'#notify-section', featured:false, soon:true,
              },
            ].map(({ name,price,period,desc,features,cta,link,featured,soon },i) => (
              <div key={name} className={`reveal reveal-${i+1}`}>
                <div style={{ background:card,border:`${featured?'2px':'1px'} solid ${featured?C.orange:cardB}`,borderRadius:R.panel,padding:'34px 30px',position:'relative',overflow:'hidden' }}>
                  <div style={{ position:'relative',zIndex:2 }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
                      <span style={{ ...TYPE.label,color:featured?'#ea580c':muted }}>{name}</span>
                      {soon&&!featured&&<span style={{ ...TYPE.label,letterSpacing:'normal',background:C.raised,color:muted,padding:'3px 8px',borderRadius:R.pill }}>SOON</span>}
                    </div>
                    <div style={{ display:'flex',alignItems:'baseline',gap:4,marginBottom:6 }}>
                      <span style={{ ...TYPE.displayM,color:head }}>{price}</span>
                      <span style={{ ...TYPE.bodyS,color:muted }}>{period}</span>
                    </div>
                    <p style={{ ...TYPE.bodyS,color:muted,lineHeight:1.6,marginBottom:22 }}>{desc}</p>
                    <ul style={{ listStyle:'none',padding:0,margin:'0 0 26px',display:'flex',flexDirection:'column',gap:11 }}>
                      {features.map(f=>(
                        <li key={f} style={{ display:'flex',alignItems:'center',gap:8,...TYPE.bodyS,color:C.ink }}>
                          <CheckCircle size={14} color={featured?'#ea580c':'#22c55e'} style={{ flexShrink:0 }}/>{f}
                        </li>
                      ))}
                    </ul>
                    {soon ? (
                      <div style={{ display:'block',textAlign:'center',padding:'13px 0',borderRadius:R.button,background:C.raised,color:muted,fontWeight:700,...TYPE.bodyS }}>Coming soon</div>
                    ) : (
                      <Link to={link} onClick={() => track('pricing_cta',{tier:name})}
                        style={{ display:'block',textAlign:'center',padding:'13px 0',borderRadius:R.button,background:C.orange,color:'#fff',fontWeight:700,...TYPE.bodyS,textDecoration:'none' }}>
                        {cta}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign:'center',...TYPE.bodyS,color:muted,marginTop:20 }}>Pricing indicative, not yet live. Starting is and will remain free.</p>
        </div>
      </section>

      {/* ── FAQ (three questions, read at the moment of decision) ──
          Full list lives at /faq. These three — what's live, what a rep
          is, why it's free — are the ones worth reading right before the
          final CTA rather than making someone click away for them. */}
      <section id="faq" className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[720px] mx-auto">
          <Plate reduced={reduced} label="Open Questions" title="Before you start"/>
          <div style={{ borderTop:`1px solid ${cardB}` }}>
            {FAQ_DATA.filter((f) => LANDING_INLINE_FAQ_IDS.includes(f.id))
              .map((f,i)=><FAQItem key={f.id} q={f.q} a={f.a} n={i+1}/>)}
          </div>
          <p style={{ textAlign:'center',...TYPE.bodyS,color:muted,marginTop:24 }}>
            <Link to="/faq" style={{ color:C.orange,textDecoration:'none',fontWeight:600 }}>More questions →</Link>
          </p>
        </div>
      </section>

      {/* ── NOTIFY ── */}
      <section id="notify-section" className="px-6 py-12 reveal">
        <div className="max-w-[720px] mx-auto">
          <div style={{ background:C.surface,border:`1px solid ${C.rule}`,borderRadius:R.hero,padding:'clamp(28px,4vw,40px)',display:'flex',flexDirection:'column',gap:20,position:'relative',overflow:'hidden' }}>
            <div style={{ position:'relative',zIndex:2 }}>
              <div style={{ ...TYPE.label,color:'#ea580c',marginBottom:10 }}>Stay in the loop</div>
              <p style={{ ...TYPE.bodyS,color:muted,lineHeight:1.7,margin:0 }}>Get the first lesson of every new skill, free, before it launches. Technical Translation is next.</p>
            </div>
            <div style={{ position:'relative',zIndex:2 }}><NotifyForm/></div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="px-6 py-20 md:py-28 reveal">
        <div className="max-w-[880px] mx-auto text-center">
          <div style={{ background:C.surface,border:`1px solid ${C.rule}`,borderRadius:R.hero,padding:'clamp(48px,7vw,80px) clamp(28px,5vw,56px)',position:'relative',overflow:'hidden',boxShadow:'0 28px 80px -16px rgba(0,0,0,0.55)' }}>
            <GridBG opacity={0.8}/>
            <div style={{ position:'relative',zIndex:2 }}>
              <div style={{ display:'inline-flex',alignItems:'center',gap:7,padding:'6px 14px',borderRadius:99,background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.25)',marginBottom:24 }}>
                <div style={{ width:6,height:6,borderRadius:'50%',background:'#22c55e',boxShadow:'0 0 6px #22c55e' }}/>
                <span style={{ ...TYPE.bodyS,fontWeight:700,color:'#22c55e' }}>Phase 1 is live now</span>
              </div>
              <h2 style={{ ...TYPE.displayL,color:'#fff',marginBottom:16 }}>Ready to stop being a passenger?</h2>
              <p style={{ ...TYPE.bodyL,color:C.graphite,maxWidth:440,margin:'0 auto 32px' }}>
                Your first rep takes 45 seconds. One brief, one answer, one number with the
                reason behind it.
              </p>
              <Link to={user?'/dashboard':'/signup'} onClick={() => track('final_cta')}
                style={{ display:'inline-flex',alignItems:'center',gap:9,background:C.orange,color:'#fff',fontWeight:700,...TYPE.bodyL,padding:'16px 36px',borderRadius:R.button,textDecoration:'none' }}>
                {user?'Go to dashboard':'Do your first rep'} <ArrowRight size={20} strokeWidth={2.5}/>
              </Link>
              <p style={{ ...TYPE.monoS,textTransform:'uppercase',color:C.dim,marginTop:18 }}>45 seconds · No card · Nothing to install</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop:`1px solid ${cardB}`,padding:'48px 24px 36px' }}>
        <div className="max-w-[880px] mx-auto" style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:24 }}>
          <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:12 }}>
            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
              <CastleLogoMark size={26}/>
              <span style={{ ...TYPE.heading }}>
                <span style={{ color:head }}>Castle </span>
                <span style={{ color:'#ea580c' }}>Atlas</span>
              </span>
            </div>
            <p style={{ ...TYPE.bodyS,color:muted,lineHeight:1.7,textAlign:'center',maxWidth:320 }}>
              The reps-based platform for skills that pay. AI scoring. Voice roleplay. Built for people who execute.
            </p>
          </div>

          <div style={{ display:'flex',alignItems:'center',gap:28,flexWrap:'wrap',justifyContent:'center' }}>
            <Link to="/about" style={{ ...TYPE.bodyS,color:muted,textDecoration:'none' }}
              onMouseEnter={e=>e.target.style.color='#ea580c'} onMouseLeave={e=>e.target.style.color=muted}>About</Link>
            <Link to="/faq" style={{ ...TYPE.bodyS,color:muted,textDecoration:'none' }}
              onMouseEnter={e=>e.target.style.color='#ea580c'} onMouseLeave={e=>e.target.style.color=muted}>FAQ</Link>
            <Link to="/contact" style={{ ...TYPE.bodyS,color:muted,textDecoration:'none' }}
              onMouseEnter={e=>e.target.style.color='#ea580c'} onMouseLeave={e=>e.target.style.color=muted}>Contact</Link>
            <Link to="/privacy" style={{ ...TYPE.bodyS,color:muted,textDecoration:'none' }}
              onMouseEnter={e=>e.target.style.color='#ea580c'} onMouseLeave={e=>e.target.style.color=muted}>Privacy Policy</Link>
            <Link to="/terms" style={{ ...TYPE.bodyS,color:muted,textDecoration:'none' }}
              onMouseEnter={e=>e.target.style.color='#ea580c'} onMouseLeave={e=>e.target.style.color=muted}>Terms of Service</Link>
            <a href="https://x.com/castleatlas" target="_blank" rel="noopener noreferrer" aria-label="Castle Atlas on X" style={{ color:muted }}
              onMouseEnter={e=>e.currentTarget.style.color='#ea580c'} onMouseLeave={e=>e.currentTarget.style.color=muted}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
          </div>

          <p style={{ ...TYPE.label,letterSpacing:'normal',color:C.dim }}>&copy; 2026 Castle Atlas</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
